import React, { useState, createContext, useContext } from 'react';
import { TimeEntry, ResourceName } from '../types/timeTracking';
import { timeEntries as seedTimeEntries } from '../utils/timeTrackingSeedData';
interface TimeTrackingContextType {
  timeEntries: TimeEntry[];
  getTimeEntriesByCompanyId: (companyId: string) => TimeEntry[];
  getTimeEntriesByProjectId: (projectId: string) => TimeEntry[];
  addTimeEntry: (entry: Omit<TimeEntry, 'id' | 'created_at'>) => void;
  updateTimeEntry: (id: string, updates: Partial<TimeEntry>) => void;
  deleteTimeEntry: (id: string) => void;
}
const TimeTrackingContext = createContext<TimeTrackingContextType | undefined>(undefined);
export function TimeTrackingProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>(seedTimeEntries);
  const getTimeEntriesByCompanyId = (companyId: string) => {
    return timeEntries.filter(entry => entry.company_id === companyId);
  };
  const getTimeEntriesByProjectId = (projectId: string) => {
    return timeEntries.filter(entry => entry.project_id === projectId);
  };
  const addTimeEntry = (entryData: Omit<TimeEntry, 'id' | 'created_at'>) => {
    const newEntry: TimeEntry = {
      ...entryData,
      id: `time-${Date.now()}`,
      created_at: new Date()
    };
    setTimeEntries([newEntry, ...timeEntries]);
  };
  const updateTimeEntry = (id: string, updates: Partial<TimeEntry>) => {
    setTimeEntries(timeEntries.map(entry => entry.id === id ? {
      ...entry,
      ...updates
    } : entry));
  };
  const deleteTimeEntry = (id: string) => {
    setTimeEntries(timeEntries.filter(entry => entry.id !== id));
  };
  const value: TimeTrackingContextType = {
    timeEntries,
    getTimeEntriesByCompanyId,
    getTimeEntriesByProjectId,
    addTimeEntry,
    updateTimeEntry,
    deleteTimeEntry
  };
  return <TimeTrackingContext.Provider value={value} data-id="element-390">
      {children}
    </TimeTrackingContext.Provider>;
}
export function useTimeTracking(): TimeTrackingContextType {
  const context = useContext(TimeTrackingContext);
  if (!context) {
    throw new Error('useTimeTracking must be used within a TimeTrackingProvider');
  }
  return context;
}